import { EZ_SEARCH_NL_LANES, NL_LANE_TO_EZ_SCOPE } from '../../utils/naturalLanguage';
import { mapNlResponseToFilters } from '../../utils/naturalLanguage/mapNlResponseToFilters';
import { getFilterParams } from '../../utils/filterUtils';

/** Chip keys that write to a different filter field than their chip.key */
const CHIP_KEY_FILTER_FIELDS = {
  hasSetAside: ['hasSetAside', 'setAside'],
  text: ['text', 'keywords'],
  keywords: ['text', 'keywords'],
};

/**
 * Normalizes multi-lane /v1/nlq/ez response:
 * - Keeps only the 8 EZ Search categories (drops federal-agents, nsn, etc.)
 * - Initializes missing EZ lanes as unanswerable
 * - Maps answerable lanes using mapNlResponseToFilters
 * - Aggregates deduplicated applied filter chips
 * - Extracts per-category match explanations
 *
 * @param {object} interpretResponse
 * @param {string} query
 * @returns {{ categoryFilters: object, explanations: object, laneStatus: object, appliedFilters: Array }}
 */
export function processEzInterpretResponse(interpretResponse, query) {
  const results = interpretResponse?.results || [];
  const categoryFilters = {};
  const explanations = {};
  const laneStatus = {};
  const rawChips = [];

  // Default every EZ scope to unanswerable so missing gateway lanes stay empty.
  EZ_SEARCH_NL_LANES.forEach(({ scope }) => {
    categoryFilters[scope] = null;
    laneStatus[scope] = 'unanswerable';
    explanations[scope] = '';
  });

  results.forEach((item) => {
    const scope = NL_LANE_TO_EZ_SCOPE[item.lane];
    if (!scope) return; // Drop non-EZ lanes (e.g. federal-agents, nsn-opportunities)

    // Only explicit answerable:true is searchable. false/undefined/missing → empty category.
    if (item.answerable === true) {
      const mapped = mapNlResponseToFilters(item.lane, item);
      categoryFilters[scope] = mapped.filters || {};
      laneStatus[scope] = 'ok';

      const explanationKeyword =
        mapped.filters?.text || item.q || (Array.isArray(item.filters?.keywords) ? item.filters.keywords[0] : null);

      explanations[scope] = explanationKeyword ? `Matches "${explanationKeyword}"` : item.interpretation || '';

      (mapped.chips || []).forEach((chip) => {
        rawChips.push({ ...chip, scope });
      });
    } else {
      categoryFilters[scope] = null;
      laneStatus[scope] = 'unanswerable';
      explanations[scope] = item.interpretation || 'No direct match in this category';
    }
  });

  // Deduplicate chips across categories by label + value
  const seenChipKeys = new Set();
  const appliedFilters = [];

  rawChips.forEach((chip) => {
    const uniqueKey = `${chip.label}:${chip.value}`.toLowerCase();
    if (!seenChipKeys.has(uniqueKey)) {
      seenChipKeys.add(uniqueKey);
      appliedFilters.push({
        id: uniqueKey,
        key: chip.key,
        label: chip.label,
        value: chip.value,
      });
    }
  });

  return {
    categoryFilters,
    explanations,
    laneStatus,
    appliedFilters,
  };
}

/**
 * Force unanswerable / non-ok scopes to empty slices and recompute totalCount
 * over the 8 EZ scopes only.
 */
export function mergeSearchResultsWithLaneStatus(searchResults, laneStatus) {
  const results = {};
  let totalCount = 0;

  EZ_SEARCH_NL_LANES.forEach(({ scope }) => {
    if (laneStatus?.[scope] === 'ok') {
      const slice = searchResults?.[scope] || { count: 0, items: [] };
      results[scope] = slice;
      totalCount += Number(slice.count) || 0;
    } else {
      results[scope] = { count: 0, items: [] };
    }
  });

  return { results, totalCount };
}

/**
 * Strips a dismissed filter chip from all scope filter configurations
 * so re-running search excludes that filter constraint.
 *
 * @param {{ key: string, label: string, value: string }} chipToRemove
 * @param {object} categoryFilters
 * @returns {object} updated category filters
 */
export function stripChipFromCategoryFilters(chipToRemove, categoryFilters) {
  const updated = {};
  Object.entries(categoryFilters || {}).forEach(([scope, filters]) => {
    if (!filters) {
      updated[scope] = null;
      return;
    }
    const cloned = { ...filters };
    const { key } = chipToRemove;
    const fieldsToClear = CHIP_KEY_FILTER_FIELDS[key] || [key];

    fieldsToClear.forEach((field) => {
      if (cloned[field] !== undefined) {
        delete cloned[field];
      }
    });

    updated[scope] = cloned;
  });
  return updated;
}

/**
 * Builds scopes payload to send to ezSearch.search.
 * Always includes all 8 EZ scopes:
 * - answerable → list-page API shape (top-level text + getFilterParams fields)
 * - unanswerable / missing → `{ skip: true }` so Node never falls back to shared query text
 *
 * @param {object} categoryFilters
 * @param {string} fallbackText
 * @returns {object}
 */
export function buildScopesPayload(categoryFilters, fallbackText) {
  const scopes = {};

  EZ_SEARCH_NL_LANES.forEach(({ scope }) => {
    const mapped = categoryFilters?.[scope];
    if (!mapped) {
      // Explicit skip — do not omit the key (omission used to fall back to keyword text on Node)
      scopes[scope] = { skip: true };
      return;
    }

    const { text, reset, ...fieldFilters } = mapped;
    const trimmed = text == null ? '' : String(text).trim();
    const searchText = trimmed || fallbackText || '';

    scopes[scope] = {
      filters: getFilterParams(fieldFilters),
      text: searchText,
    };
  });

  return scopes;
}

export function canRefineScope(laneStatus, categoryFilters, scope) {
  return laneStatus?.[scope] === 'ok' && categoryFilters?.[scope] != null;
}
